import axios from 'axios';
import { BACKEND_URL } from '../constants';

// Created with support for member verification tokens
const axiosConfig = {
  baseURL: BACKEND_URL,
  headers: {
    'Content-Type': 'application/json',
    'timezone': Intl.DateTimeFormat().resolvedOptions().timeZone
  },
};

const api = axios.create(axiosConfig);

// Attach the member-verification token (if present) to outgoing requests.
// Note: token capture from the URL is done explicitly at the entry-point
// route (MemberVerification view) so that an attacker cannot trick the
// browser into adopting a token by linking to any page with a ?token= param.
api.interceptors.request.use((config) => {
  const verificationToken = localStorage.getItem('memberVerificationToken');
  if (verificationToken) {
    config.headers.Authorization = `Bearer ${verificationToken}`;
  }
  return config;
});

// Global 401 handler. Previously the app kept rendering authenticated views
// even after the backend session expired — the user saw blank/error data
// instead of being redirected to login. We wipe the stored tokens, broadcast
// a session-expired event so any in-tree provider (JWTAuthContext) can clear
// React state, and route to /login.
//
// Skipped for member-verification routes because their 401s mean "this OTP
// token expired" and the verification view handles that itself with a more
// specific UX (re-prompt, fallback to email).
const MEMBER_VERIFICATION_PATH_REGEX = /\/(public|verification|api\/verify|api\/quality|api\/liveliness)\//;

let _redirectingForAuthExpiry = false;
api.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error?.response?.status;
    const url = error?.config?.url || '';
    if (
      status === 401 &&
      !MEMBER_VERIFICATION_PATH_REGEX.test(url) &&
      !_redirectingForAuthExpiry
    ) {
      _redirectingForAuthExpiry = true;
      try {
        localStorage.removeItem('accessToken');
        // Broadcast so JWTAuthContext (and any other auth listeners) can
        // clear in-memory user state and toast "session expired".
        window.dispatchEvent(new CustomEvent('auth:session-expired'));
      } catch (_) { /* private-mode storage / no-window — swallow */ }
      // Only redirect when not already on /login (otherwise we cause a
      // useless reload loop on a failed login attempt).
      try {
        if (
          typeof window !== 'undefined' &&
          window.location &&
          !window.location.pathname.startsWith('/login')
        ) {
          // Use replace() so the back button doesn't bounce the user
          // straight back into the expired session.
          window.location.replace('/login');
        }
      } catch (_) { /* swallow */ }
      // Clear the guard on the next tick so future genuine 401s (after a
      // re-login) are handled again.
      setTimeout(() => { _redirectingForAuthExpiry = false; }, 5000);
    }
    return Promise.reject(error);
  }
);

export default api;
